/**
 * Réunion libre — Phase D (invités anonymes, migration 0259). Contrairement
 * aux autres types de rendez-vous (`_app.tutor.rendez-vous.new.tsx`, RPC
 * `create_direct_rendezvous`, un élève ciblé), une réunion libre n'a AUCUNE
 * réservation ni élève choisi à l'avance : le tuteur crée la salle
 * (`create_visio_meeting`) et PARTAGE un lien (`create_visio_invite`) — qui
 * que ce soit qui le suit rejoint (compte réel connecté, ou session anonyme si
 * `guests_enabled`), en salle d'attente si `lobby_enabled`. L'hôte est admis
 * d'office à son propre roster par la RPC — « Rejoindre maintenant » navigue
 * directement vers `/reunion/$roomId/room`, aucun round-trip RPC.
 */
import { useState } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { InviteLinkField } from '@/components/invite-link-field';
import { buildVisioInviteUrl } from '@/lib/visio/tutor-url';
import { supabase } from '@/lib/supabase';

// `create_visio_meeting`/`create_visio_invite` sont postérieures aux types
// générés (comme `get_or_create_visio_room`, cf. CLAUDE.md) — client générique.
const db = supabase as unknown as SupabaseClient;

const DURATIONS = [15, 30, 45, 60, 90, 120] as const;
const CAPACITIES = [2, 4, 6, 8, 10, 12] as const;

interface MeetingResult {
  roomId: string;
  shareUrl: string;
}

export function TutorNewMeetingPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [duration, setDuration] = useState(60);
  const [maxParticipants, setMaxParticipants] = useState(8);
  const [guestsEnabled, setGuestsEnabled] = useState(true);
  const [lobbyEnabled, setLobbyEnabled] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MeetingResult | null>(null);

  async function handleCreate() {
    setError(null);
    const trimmed = title.trim();
    if (!trimmed) {
      setError('Donne un titre à ta réunion.');
      return;
    }
    setCreating(true);
    try {
      const { data: meetingRows, error: meetingError } = await db.rpc('create_visio_meeting', {
        p_title: trimmed,
        p_duration_minutes: duration,
        p_max_participants: maxParticipants,
        p_guests_enabled: guestsEnabled,
        p_lobby_enabled: lobbyEnabled,
      });
      if (meetingError) throw new Error(meetingError.message);
      const roomId = (meetingRows as Array<{ room_id: string }> | null)?.[0]?.room_id;
      if (!roomId) throw new Error('missing_room_id');

      const { data: inviteRows, error: inviteError } = await db.rpc('create_visio_invite', {
        p_room_id: roomId,
      });
      if (inviteError) throw new Error(inviteError.message);
      const token = (inviteRows as Array<{ token: string }> | null)?.[0]?.token;
      if (!token) throw new Error('missing_token');

      setResult({ roomId, shareUrl: buildVisioInviteUrl(token) });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Création impossible.');
    } finally {
      setCreating(false);
    }
  }

  if (result) {
    return (
      <div className="mx-auto max-w-lg px-4 py-6 sm:py-8">
        <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
          Réunion créée
        </h1>
        <div className="border-ink-100 mt-5 space-y-4 rounded-2xl border bg-white p-5">
          <p className="text-ink-500 text-sm">
            Partage ce lien : quiconque le suit peut rejoindre
            {guestsEnabled ? ', même sans compte' : ''}
            {lobbyEnabled ? ' — tu devras admettre chaque personne.' : '.'}
          </p>
          <InviteLinkField url={result.shareUrl} shareTitle="Rejoins ma réunion" />
          <Button
            onClick={() =>
              void navigate({ to: '/reunion/$roomId/room', params: { roomId: result.roomId } })
            }
            className="min-h-[2.75rem] w-full active:scale-[0.98]"
          >
            Rejoindre maintenant
          </Button>
          <button
            type="button"
            onClick={() => void navigate({ to: '/tutor/rendez-vous' })}
            className="text-primary-600 block w-full text-center text-sm font-semibold"
          >
            Retour aux rendez-vous
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 sm:py-8">
      <button
        type="button"
        onClick={() => void navigate({ to: '/tutor/rendez-vous/new' })}
        aria-label="Retour"
        className="border-ink-200 text-ink-700 hover:bg-ink-50 mb-3 flex h-11 w-11 items-center justify-center rounded-full border bg-white shadow-sm transition-colors active:scale-95"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <h1 className="font-display text-ink-900 text-2xl font-extrabold tracking-tight">
        Réunion libre
      </h1>
      <p className="text-ink-500 mt-1 text-sm">
        Aucun élève à choisir : tu partages un lien, et qui que ce soit qui le suit peut rejoindre.
      </p>

      <div className="border-ink-100 mt-5 space-y-4 rounded-2xl border bg-white p-5">
        <div>
          <label
            htmlFor="meeting-title"
            className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
          >
            Titre
          </label>
          <input
            id="meeting-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Réunion de classe, atelier révisions…"
            maxLength={120}
            className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
          />
        </div>

        <div>
          <label
            htmlFor="meeting-duration"
            className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
          >
            Durée
          </label>
          <select
            id="meeting-duration"
            value={duration}
            onChange={(e) => setDuration(Number(e.target.value))}
            className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
          >
            {DURATIONS.map((m) => (
              <option key={m} value={m}>
                {m} min
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="meeting-capacity"
            className="font-display text-ink-700 mb-1.5 block text-xs font-semibold"
          >
            Nombre de participants max
          </label>
          <select
            id="meeting-capacity"
            value={maxParticipants}
            onChange={(e) => setMaxParticipants(Number(e.target.value))}
            className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-11 w-full appearance-none rounded-xl border px-3.5 font-sans text-sm transition-colors focus:outline-none"
          >
            {CAPACITIES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => setGuestsEnabled((v) => !v)}
          className={cn(
            'flex min-h-[2.75rem] w-full items-center justify-between rounded-xl border px-3.5 text-left transition-colors active:scale-[0.98]',
            guestsEnabled ? 'border-primary-500 bg-primary-50' : 'border-ink-200 bg-white',
          )}
          aria-pressed={guestsEnabled}
        >
          <span>
            <span className="text-ink-900 block text-sm font-semibold">Autoriser les invités</span>
            <span className="text-ink-500 block text-xs">
              Sans compte JeRaisonne — juste un nom.
            </span>
          </span>
          <span
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full border-2',
              guestsEnabled ? 'border-primary-500 bg-primary-500' : 'border-ink-300',
            )}
          >
            {guestsEnabled && <span className="size-2.5 rounded-full bg-white" />}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setLobbyEnabled((v) => !v)}
          className={cn(
            'flex min-h-[2.75rem] w-full items-center justify-between rounded-xl border px-3.5 text-left transition-colors active:scale-[0.98]',
            lobbyEnabled ? 'border-primary-500 bg-primary-50' : 'border-ink-200 bg-white',
          )}
          aria-pressed={lobbyEnabled}
        >
          <span>
            <span className="text-ink-900 block text-sm font-semibold">Salle d’attente</span>
            <span className="text-ink-500 block text-xs">
              Tu admets chaque personne avant qu’elle n’entre.
            </span>
          </span>
          <span
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full border-2',
              lobbyEnabled ? 'border-primary-500 bg-primary-500' : 'border-ink-300',
            )}
          >
            {lobbyEnabled && <span className="size-2.5 rounded-full bg-white" />}
          </span>
        </button>

        {error && <p className="text-accent-600 font-sans text-xs">{error}</p>}

        <Button onClick={() => void handleCreate()} disabled={creating} className="w-full">
          {creating ? 'Création…' : 'Créer la réunion'}
        </Button>
      </div>
    </div>
  );
}
